import React from 'react';
import type { Product } from '../types';
import './ProductCard.css';

interface ProductCardProps {
  product: Product;
  cartQuantity?: number;
  cartItemId?: number;
  onAddToCart: (productId: number) => void;
  onUpdateQuantity: (itemId: number, quantity: number) => void;
  onProductClick: (productId: number) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  cartQuantity = 0,
  cartItemId,
  onAddToCart,
  onUpdateQuantity,
  onProductClick,
}) => {
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart(product.id);
  };

  const handleIncrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (cartItemId) {
      onUpdateQuantity(cartItemId, cartQuantity + 1);
    }
  };

  const handleDecrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (cartItemId && cartQuantity > 1) {
      onUpdateQuantity(cartItemId, cartQuantity - 1);
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const formatQuantityInfo = () => {
    const parts: string[] = [];
    if (product.weight) {
      parts.push(`${product.weight}г`);
    }
    if (product.pieces) {
      parts.push(`${product.pieces} шт`);
    }
    return parts.join(' • ') || '';
  };

  const isInCart = cartQuantity > 0;

  return (
    <div
      className="product-card"
      onClick={() => onProductClick(product.id)}
    >
      <div className="product-image">
        <img
          src={product.image_url}
          alt={product.name}
          onError={(e) => {
            e.currentTarget.src = 'https://via.placeholder.com/300x200?text=No+Image';
          }}
        />
        {!product.is_available && (
          <div className="product-unavailable">Нет в наличии</div>
        )}
      </div>

      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>

        {formatQuantityInfo() && (
          <div className="product-weight">{formatQuantityInfo()}</div>
        )}

        <div className="product-footer">
          <div className="product-price">{formatPrice(product.price)}</div>

          {isInCart ? (
            <div className="quantity-controls-inline">
              <button
                className="quantity-btn-inline"
                onClick={handleDecrease}
                disabled={cartQuantity <= 1}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <span className="quantity-value-inline">{cartQuantity}</span>
              <button
                className="quantity-btn-inline"
                onClick={handleIncrease}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <button
              className="add-to-cart-btn"
              onClick={handleAddClick}
              disabled={!product.is_available}
            >
              В корзину
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
